// src/pages/VehicleV2/VehicleTdsPercentListing.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaTrash, FaSave, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";
import axios from "axios";

const LIST_API = "https://nissi-india.com/api/tdsPercentListing";
const ENTRY_API = "https://nissi-india.com/api/tdsPercent";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const pad2 = (n) => String(n).padStart(2, "0");

const ddmmyyyyToYmd = (ddmmyyyy) => {
  const v = toStr(ddmmyyyy);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const ymdToDDMMYYYY = (ymd) => {
  const v = toStr(ymd);
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const todayToYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const todayToDDMMYYYY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isValidDDMMYYYY = (s) => {
  const v = toStr(s);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

const clampTds = (raw) => {
  let v = toStr(raw).replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join("")}`;
  const [a, b] = v.split(".");
  if (typeof b === "string") v = `${a}.${b.slice(0, 2)}`;
  if (v.length > 6) v = v.slice(0, 6);
  return v;
};

const ddmmyyyyToDisplay = (value) => {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-GB");
};

const VehicleTdsPercentListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const canDelete = useMemo(() => {
    const v = sessionStorage.getItem("tdspercent_deleterights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v =
      sessionStorage.getItem("BranchActive") ||
      localStorage.getItem("BranchActive") ||
      localStorage.getItem("branchactive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const todayYmd = useMemo(() => todayToYMD(), []);

  const [showForm, setShowForm] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tdsPercentId, setTdsPercentId] = useState(0);
  const [vehicleSubtypeId, setVehicleSubtypeId] = useState("Select");
  const [dateStr, setDateStr] = useState(todayToDDMMYYYY());
  const [tdsPercent, setTdsPercent] = useState("");
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [showSubmit, setShowSubmit] = useState(true);
  const isEdit = tdsPercentId > 0;

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const resetForm = useCallback(() => {
    setTdsPercentId(0);
    setVehicleSubtypeId("Select");
    setDateStr(todayToDDMMYYYY());
    setTdsPercent("");
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ENTRY_API}/config`, {
        params: { branchActive: branchActive ? 1 : 0 },
        headers: commonHeaders,
      });
      setShowSubmit(Boolean(data?.showSubmit ?? true));
    } catch (_e) {
      setShowSubmit(true);
    }
  }, [branchActive, commonHeaders]);

  const loadVehicleTypes = useCallback(
    async (editMode = false) => {
      try {
        const { data } = await axios.get(`${ENTRY_API}/vehicle-types`, {
          params: { mode: editMode ? "edit" : "add" },
          headers: commonHeaders,
        });
        const list = Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : [];
        setVehicleTypes(
          [{ id: "Select", name: "Select" }].concat(
            list.map((x) => ({ id: String(x.id), name: String(x.name) }))
          )
        );
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load vehicle types");
        setVehicleTypes([{ id: "Select", name: "Select" }]);
      }
    },
    [commonHeaders]
  );

  const fetchList = useCallback(
    async (pageNo = page, pageSize = limit, searchText = search) => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${LIST_API}/list`, {
          params: { page: pageNo, limit: pageSize, search: toStr(searchText), loginUserId },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load data");
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(toInt(data?.totalRecords ?? data?.total, 0));
        setTotalPages(toInt(data?.totalPages, 1) || 1);
        setPage(toInt(data?.currentPage ?? data?.page, pageNo));
        setLimit(toInt(data?.pageSize ?? data?.limit, pageSize));
        setSelectedIds([]);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Server error");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, loginUserId, commonHeaders]
  );

  useEffect(() => {
    loadConfig();
    loadVehicleTypes(false);
    fetchList(1, limit, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const handleEdit = async (row) => {
    const id = toInt(row?.tdsPercentId);
    if (!id) return;
    setShowForm(true);
    setTdsPercentId(id);
    await loadVehicleTypes(true);
    try {
      const { data } = await axios.get(`${ENTRY_API}/view`, {
        params: { tdsId: id },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load TDS percent");
      const d = data?.data || {};
      setVehicleSubtypeId(d.subtypeId != null ? String(d.subtypeId) : "Select");
      setDateStr(toStr(d.date) || ddmmyyyyToDisplay(row.date));
      setTdsPercent(d.tdsPercent != null ? String(d.tdsPercent) : String(row.tdsPercent || ""));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load TDS percent");
    }
  };

  const validate = () => {
    if (!branchActive) return toast.error("Branch is not active. Save not allowed."), false;
    if (!showSubmit) return toast.error("Submit is disabled."), false;
    if (String(vehicleSubtypeId) === "Select") return toast.error("Please select vehicle type"), false;
    if (!toStr(dateStr) || !isValidDDMMYYYY(dateStr)) return toast.error("Invalid date"), false;
    const [dd, mm, yyyy] = dateStr.split("/").map(Number);
    const selectedDate = new Date(yyyy, mm - 1, dd);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate.getTime() > today.getTime()) {
      toast.error("Select Current Date or Previous date");
      return false;
    }
    const percentNum = Number(tdsPercent);
    if (!Number.isFinite(percentNum) || percentNum <= 0) {
      toast.error("Please enter TDS Percentage greater than 0");
      return false;
    }
    if (percentNum > 100) {
      toast.error("Please enter TDS Percentage less than or equal to 100");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${ENTRY_API}/save`,
        {
          tdsId: tdsPercentId || 0,
          vehicleSubtypeId: toInt(vehicleSubtypeId, 0),
          date: toStr(dateStr),
          tdsPercent: Number(tdsPercent),
          loginUserId,
          branchId,
          branchType: branch,
          branchActive: branchActive ? 1 : 0,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Failed to save TDS percent");
      toast.success(data?.message || "Saved successfully");
      resetForm();
      await loadVehicleTypes(false);
      await fetchList(page, limit, search);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save TDS percent");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!branchActive) return toast.error("Branch is not active. Delete not allowed.");
    if (!canDelete) return toast.error("You do not have rights to delete TDS percent.");
    if (!selectedIds.length) return toast.warn("Please select any one...");
    if (!window.confirm("Are you sure to delete?")) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${LIST_API}/delete`,
        { ids: selectedIds, loginUserId },
        { headers: commonHeaders }
      );
      if (data?.success) toast.success(data?.message || "Deleted successfully");
      else toast.error(data?.message || "Delete failed");
      if (selectedIds.includes(tdsPercentId)) resetForm();
      const deletingAll = rows.length === selectedIds.length;
      await fetchList(page > 1 && deletingAll ? page - 1 : page, limit, search);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.tdsPercentId));

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TDS PERCENT</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle TDS Percent
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              
              <button
                onClick={() => setShowForm((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showForm && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  {isEdit ? "Edit TDS Percent" : "Add TDS Percent"}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={resetForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2"><FaTimes /> Clear</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !showSubmit || !branchActive}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                      saving ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Vehicle Type *</label>
                  <select
                    value={vehicleSubtypeId}
                    onChange={(e) => setVehicleSubtypeId(e.target.value)}
                    className={inputClass}
                    disabled={saving || isEdit}
                  >
                    {vehicleTypes.map((x) => (
                      <option key={x.id} value={String(x.id)}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Date *</label>
                  <input
                    type="date"
                    value={ddmmyyyyToYmd(dateStr)}
                    max={todayYmd}
                    onChange={(e) => setDateStr(ymdToDDMMYYYY(e.target.value))}
                    className={inputClass}
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className={labelClass}>TDS Percent *</label>
                  <input
                    type="text"
                    value={tdsPercent}
                    onChange={(e) => setTdsPercent(clampTds(e.target.value))}
                    className={inputClass}
                    placeholder="Enter TDS percent..."
                    disabled={saving}
                    inputMode="decimal"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSearch("");
                      fetchList(1, limit, "");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => fetchList(1, limit, search)}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchList(1, limit, search)}
                className={inputClass}
                placeholder="Search vehicle subtype / percent / date..."
              />
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>Loading...</div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No Records Found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">TDS Percent</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() =>
                            setSelectedIds(allSelected ? [] : rows.map((r) => r.tdsPercentId))
                          }
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => (
                      <tr
                        key={r.tdsPercentId}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } ${tdsPercentId === r.tdsPercentId ? (isDark ? "ring-1 ring-blue-400" : "bg-blue-50") : ""} ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                        }`}
                      >
                        <td className="px-6 py-4 text-sm">{(page - 1) * limit + idx + 1}</td>
                        <td className="px-6 py-4 text-sm">{ddmmyyyyToDisplay(r.date)}</td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.vehicleSubTypeName || ""}
                        </td>
                        <td className="px-6 py-4 text-sm">{r.tdsPercent ?? ""}</td>
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(r)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg"
                          >
                            ✏️
                          </button>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.tdsPercentId)}
                            onChange={() =>
                              setSelectedIds((p) =>
                                p.includes(r.tdsPercentId)
                                  ? p.filter((x) => x !== r.tdsPercentId)
                                  : [...p, r.tdsPercentId]
                              )
                            }
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {canDelete && branchActive && (
                  <div className="p-4">
                    <button
                      onClick={handleDelete}
                      disabled={!selectedIds.length}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                        selectedIds.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      <FaTrash /> Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{totalPages}</b> | Total: <b>{total}</b>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={limit}
                onChange={(e) => fetchList(1, Number(e.target.value || 10), search)}
                className={inputClass.replace("w-full ", "w-auto ")}
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <button
                disabled={page <= 1}
                onClick={() => fetchList(page - 1, limit, search)}
                className={`px-3 py-2 border rounded-lg ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""} ${
                  isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => fetchList(page + 1, limit, search)}
                className={`px-3 py-2 border rounded-lg ${
                  page >= totalPages ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTdsPercentListing;